import React, { useState, useEffect } from "react";
import { LinearProgress, Typography, Button, Box } from "@mui/material";

function FeedbackComponent({
  empathyScore = 0,
  activeObservationScore = 0,
  highStakesScore = 0,
  varyingOpinionsScore = 0,
  feedbackText = "",
}) {
  const [showFullFeedback, setShowFullFeedback] = useState(false);
  const [formattedFeedback, setFormattedFeedback] = useState("");

  const getProgressValue = (score) => (score / 5) * 100;

  // Gradient Colors
  const colors = [
    "rgb(255, 0, 0)", // Red
    "rgb(255, 165, 0)", // Orange
    "rgb(255, 255, 0)", // Yellow
    "rgb(255, 255, 224)", // Light Lemon
    "rgb(0, 128, 0)", // Green
  ];

  const getGradient = (percentage) => {
    const gradientStops = [];
    const colorCount = Math.ceil(percentage / 20);

    for (let i = 0; i < colorCount; i++) {
      const color = colors[i];
      const startPercentage = i * 20;
      const endPercentage = Math.min((i + 1) * 20, percentage);

      gradientStops.push(`${color} ${startPercentage}%`);
      if (i === colorCount - 1) {
        gradientStops.push(`${color} ${endPercentage}%`);
      }
    }

    return `linear-gradient(to right, ${gradientStops.join(", ")})`;
  };

  useEffect(() => {
    if (feedbackText.length > 0) {
      const formattedText = formatResponse(feedbackText);
      setFormattedFeedback(formattedText);
    }
  }, [feedbackText]);

  const formatResponse = (response) => {
    const headers = response
      .replace(/(######\s?)(.*?)\n/g, "<h6>$2</h6>")
      .replace(/(#####\s?)(.*?)\n/g, "<h5>$2</h5>")
      .replace(/(####\s?)(.*?)\n/g, "<h4>$2</h4>")
      .replace(/(###\s?)(.*?)\n/g, "<h3>$2</h3>")
      .replace(/(##\s?)(.*?)\n/g, "<h2>$2</h2>")
      .replace(/(#\s?)(.*?)\n/g, "<h1>$2</h1>");

    let responseArray = headers.split("**");
    let newResponse = "";
    for (let i = 0; i < responseArray.length; i++) {
      if (i % 2 === 0) {
        newResponse += responseArray[i];
      } else {
        newResponse += `<b>${responseArray[i]}</b>`;
      }
    }

    newResponse = newResponse.replace(/\*/g, "<br/>");
    newResponse = newResponse.replace(/\n/g, "<br/>");
    newResponse = newResponse.replace(/\\"/g, '"');

    return newResponse;
  };

  return (
    <Box
      sx={{
        padding: "1rem",
        maxWidth: "900px",
        color: "white",
        fontFamily: "'Outfit', sans-serif",
      }}
    >
      <Typography
        variant="h5"
        sx={{
          fontWeight: "bold",
          marginBottom: "1rem",
          textAlign: "left",
          fontFamily: "'Outfit', sans-serif",
          fontSize: "1.65rem",
        }}
      >
        Feedback
      </Typography>

      {/* Feedback Rows */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
          padding: "2rem",
        }}
      >
        {[
          { label: "Empathy", score: empathyScore },
          { label: "Active Observation", score: activeObservationScore },
          { label: "High Stakes", score: highStakesScore },
          { label: "Varying Opinions", score: varyingOpinionsScore },
        ].map(({ label, score }, index) => {
          const progressValue = getProgressValue(score);

          return (
            <Box
              key={index}
              sx={{ display: "flex", alignItems: "center", gap: "1rem" }}
            >
              <Typography
                variant="body1"
                sx={{
                  minWidth: "150px",
                  fontSize: "1.05rem",
                  fontFamily: "'Outfit', sans-serif",
                }}
              >
                {label}
              </Typography>
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  height: "15px",
                  borderRadius: "5px",
                  overflow: "hidden",
                  backgroundColor: "#efc1c5db",
                }}
              >
                <Box
                  sx={{
                    height: "100%",
                    width: `${progressValue}%`,
                    transition: "width 0.3s ease-in-out",
                    background: getGradient(progressValue),
                  }}
                />
              </Box>
              <Typography
                variant="body2"
                sx={{
                  fontSize: "1rem",
                  fontFamily: "'Outfit', sans-serif",
                  minWidth: "40px",
                  textAlign: "right",
                }}
              >
                {score} / 5
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* Show Full Feedback Button */}
      <Box
        sx={{ display: "flex", justifyContent: "center", marginTop: "1.5rem" }}
      >
        <Button
          variant="contained"
          onClick={() => setShowFullFeedback(!showFullFeedback)}
          sx={{
            padding: "0.8rem 2rem",
            backgroundColor: "#FF5A5F",
            color: "white",
            fontSize: "1rem",
            fontWeight: "bold",
            borderRadius: "8px",
            border: "none",
            cursor: "pointer",
            boxShadow: "0 2px 5px rgba(0, 0, 0, 0.3)",
            textTransform: "none",
            fontFamily: "'Outfit', sans-serif",
            "&:hover": {
              backgroundColor: "#E04E4F",
            },
          }}
        >
          {showFullFeedback
            ? "Hide Full Feedback"
            : "Click to View Detailed Feedback"}
        </Button>
      </Box>

      {/* Full Feedback */}
      {showFullFeedback && (
        <Box
          sx={{
            marginTop: "1rem",
            padding: "1rem",
            borderRadius: "8px",
            fontFamily: "'Outfit', sans-serif",
            textAlign: "left",
          }}
        >
          <Typography
            variant="body1"
            sx={{
              fontSize: "1rem",
              lineHeight: "1.5",
            }}
          >
            <div dangerouslySetInnerHTML={{ __html: formattedFeedback }}></div>
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default FeedbackComponent;
